// ** React Imports
import { createContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// ** Config
import {
  SuppressionListBase,
  SuppressionListSearch,
  SuppressionListBulkDelete,
  SuppressionListBulkStatus,
  SuppressionListUsage,
  SuppressionListById,
  SuppressionListMembers,
  SuppressionListMemberById,
  SuppressionListImport,
  SuppressionListImportCommit,
  SuppressionListImportStatus,
  SuppressionListImportFail,
  SuppressionListFilters
} from 'src/configs/api'
import { useAPI } from 'src/utils/api'
import { useToast } from 'src/@core/components/custom-Toast/customToast'

// Channels + list type defined locally, mirroring the backend's
// suppressionlistdto constants (kept local there too, per the backend's
// constraint against depending on campaignsdto — see IMPLEMENTATION_NOTES.md).
export const SUPPRESSION_LIST_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'rcs', label: 'RCS' }
]

// Lowercase (retrofitted 2026-09-08 to match the backend's casing change).
export const SUPPRESSION_LIST_TYPE_STATIC = 'static'

// A blackout list applies to every promotional campaign without being picked,
// and always covers all four channels — the backend enforces both.
export const SUPPRESSION_LIST_TYPE_BLACKOUT = 'blackout'

const defaultProvider = {
  rows: [],
  setRows: () => null,
  rowCount: 0,
  setRowCount: () => null,
  selectedList: null,
  setSelectedList: () => null,
  fetchSuppressionLists: () => Promise.resolve(),
  createSuppressionList: () => Promise.resolve(null),
  getSuppressionListById: () => Promise.resolve(null),
  updateSuppressionList: () => Promise.resolve(null),
  deleteSuppressionList: () => Promise.resolve(false),
  checkSuppressionListUsage: () => Promise.resolve({ safeToDelete: [], blocked: [] }),
  bulkDeleteSuppressionLists: () =>
    Promise.resolve({ totalRequested: 0, deletedCount: 0, deletedIds: [], skippedIds: [] }),
  archiveSuppressionList: () => Promise.resolve(null),
  unarchiveSuppressionList: () => Promise.resolve(null),
  bulkArchiveSuppressionLists: () =>
    Promise.resolve({ totalRequested: 0, updatedCount: 0, updatedIds: [], skippedIds: [] }),
  bulkUnarchiveSuppressionLists: () =>
    Promise.resolve({ totalRequested: 0, updatedCount: 0, updatedIds: [], skippedIds: [] }),

  suppressionListFilterList: [],
  setSuppressionListFilterList: () => null,
  suppressionListOtherFilterList: [],
  setSuppressionListOtherFilterList: () => null,
  hasSuppressionListTagFilter: false,
  setHasSuppressionListTagFilter: () => null,
  getSuppressionListFilterList: () => Promise.resolve({}),

  members: [],
  setMembers: () => null,
  memberTotal: 0,
  setMemberTotal: () => null,
  getMembers: () => Promise.resolve(),
  updateMember: () => Promise.resolve(false),
  deleteMember: () => Promise.resolve(false),

  wizardStep: 0,
  setWizardStep: () => null,
  detailsName: '',
  setDetailsName: () => null,
  detailsChannels: [],
  setDetailsChannels: () => null,
  detailsIsBlackout: false,
  setDetailsIsBlackout: () => null,
  // The list's type as saved on the server ('' until Details' Next first
  // saves the list). The blackout flag is saved on that Next and is permanent
  // from then on, so this — not the checkbox — decides whether the checkbox
  // may still change: a saved blackout list is locked, anything else is free.
  detailsSavedType: '',
  setDetailsSavedType: () => null,
  detailsTags: [],
  setDetailsTags: () => null,
  detailsDescription: '',
  setDetailsDescription: () => null,
  detailsError: '',
  setDetailsError: () => null,

  importMethod: 'file',
  setImportMethod: () => null,
  files: [],
  setFiles: () => null,
  csvData: '',
  setCsvData: () => null,
  wizardListId: '',
  setWizardListId: () => null,
  wizardAction: 'update',
  setWizardAction: () => null,
  draftId: '',
  setDraftId: () => null,
  detectedHeaders: [],
  setDetectedHeaders: () => null,
  sampleRow: [],
  setSampleRow: () => null,
  importHeaderless: false,
  generatedMapping: {},
  setGeneratedMapping: () => null,
  userMapping: [],
  setUserMapping: () => null,
  previewSummary: null,
  setPreviewSummary: () => null,
  commitStatus: 'idle',
  setCommitStatus: () => null,
  importDraftStatus: null,
  setImportDraftStatus: () => null,
  detectImport: () => Promise.resolve(null),
  previewImport: () => Promise.resolve(null),
  commitImport: () => Promise.resolve(null),
  pollImportStatus: () => Promise.resolve(null),
  markImportFailed: () => Promise.resolve(null),
  pendingMembers: [],
  setPendingMembers: () => null,
  manualAddedCount: 0,
  setManualAddedCount: () => null,
  addMembers: () => Promise.resolve({ ok: false, results: [] }),
  resetImportWizard: () => null
}

const SuppressionListContext = createContext(defaultProvider)

const SuppressionListProvider = ({ children }) => {
  const [rows, setRows] = useState(defaultProvider.rows)
  const [rowCount, setRowCount] = useState(defaultProvider.rowCount)
  const [selectedList, setSelectedList] = useState(defaultProvider.selectedList)

  const [suppressionListFilterList, setSuppressionListFilterList] = useState(
    defaultProvider.suppressionListFilterList
  )
  const [suppressionListOtherFilterList, setSuppressionListOtherFilterList] = useState(
    defaultProvider.suppressionListOtherFilterList
  )
  const [hasSuppressionListTagFilter, setHasSuppressionListTagFilter] = useState(
    defaultProvider.hasSuppressionListTagFilter
  )

  const [members, setMembers] = useState(defaultProvider.members)
  const [memberTotal, setMemberTotal] = useState(defaultProvider.memberTotal)

  const [wizardStep, setWizardStep] = useState(defaultProvider.wizardStep)
  const [detailsName, setDetailsName] = useState(defaultProvider.detailsName)
  const [detailsChannels, setDetailsChannels] = useState(defaultProvider.detailsChannels)
  const [detailsIsBlackout, setDetailsIsBlackout] = useState(defaultProvider.detailsIsBlackout)
  const [detailsSavedType, setDetailsSavedType] = useState(defaultProvider.detailsSavedType)
  const [detailsTags, setDetailsTags] = useState(defaultProvider.detailsTags)
  const [detailsDescription, setDetailsDescription] = useState(defaultProvider.detailsDescription)
  const [detailsError, setDetailsError] = useState(defaultProvider.detailsError)

  const [importMethod, setImportMethod] = useState(defaultProvider.importMethod)
  const [files, setFiles] = useState(defaultProvider.files)
  const [csvData, setCsvData] = useState(defaultProvider.csvData)
  const [wizardListId, setWizardListId] = useState(defaultProvider.wizardListId)
  const [wizardAction, setWizardAction] = useState(defaultProvider.wizardAction)
  const [draftId, setDraftId] = useState(defaultProvider.draftId)
  const [detectedHeaders, setDetectedHeaders] = useState(defaultProvider.detectedHeaders)
  const [sampleRow, setSampleRow] = useState(defaultProvider.sampleRow)
  const [importHeaderless, setImportHeaderless] = useState(defaultProvider.importHeaderless)
  const [generatedMapping, setGeneratedMapping] = useState(defaultProvider.generatedMapping)
  const [userMapping, setUserMapping] = useState(defaultProvider.userMapping)
  const [previewSummary, setPreviewSummary] = useState(defaultProvider.previewSummary)
  const [commitStatus, setCommitStatus] = useState(defaultProvider.commitStatus)
  const [importDraftStatus, setImportDraftStatus] = useState(defaultProvider.importDraftStatus)

  // Members typed in on the Review step. They live here, not in Review, so
  // Back and Next keep them; Finish saves them before the file commits.
  const [pendingMembers, setPendingMembers] = useState(defaultProvider.pendingMembers)

  // How many typed members Finish has saved in this run, so the completion
  // message can report them with the file's rows as one outcome.
  const [manualAddedCount, setManualAddedCount] = useState(defaultProvider.manualAddedCount)

  const { showSuccess, showError } = useToast()
  const api = useAPI()

  const fetchSuppressionLists = async payload => {
    try {
      const response = await api.post(SuppressionListSearch, payload)
      setRows(response?.data?.lists ?? [])
      setRowCount(response?.data?.total ?? 0)
    } catch {
      // handled by the global toast interceptor in useAPI
    }
  }

  const createSuppressionList = async payload => {
    try {
      setDetailsError('')
      const response = await api.post(SuppressionListBase, payload)
      return response?.data ?? null
    } catch (error) {
      setDetailsError(error?.response?.data?.message || 'Could not create this list. Please try again.')
      return null
    }
  }

  const getSuppressionListById = async id => {
    try {
      const response = await api.get(SuppressionListById.replace(':listId', id))
      setSelectedList(response?.data ?? null)
      return response?.data ?? null
    } catch {
      return null
    }
  }

  // Toasts name the kind of list the user acted on — a blackout list is never
  // called a suppression list.
  const listLabel = type => (type === SUPPRESSION_LIST_TYPE_BLACKOUT ? 'Blackout list' : 'Suppression list')

  // successMessage may be a string or a function of the saved list's label;
  // the default reads the type from the saved list the API returns, so it is
  // right even on the save that converts a list to blackout. Pass null to save
  // without a toast — the wizard's Details step saves on every Next, and
  // toasting each one was noise.
  const updateSuppressionList = async (id, payload, successMessage = label => `${label} updated successfully!`) => {
    try {
      setDetailsError('')
      const response = await api.put(SuppressionListById.replace(':listId', id), payload)
      if (successMessage) {
        const label = listLabel(response?.data?.type ?? payload?.type)
        showSuccess(typeof successMessage === 'function' ? successMessage(label) : successMessage)
      }
      return response?.data ?? null
    } catch (error) {
      setDetailsError(error?.response?.data?.message || 'Could not update this list. Please try again.')
      return null
    }
  }

  const deleteSuppressionList = async (id, type) => {
    try {
      await api.delete(SuppressionListById.replace(':listId', id))
      showSuccess(`${listLabel(type)} deleted successfully!`)
      return true
    } catch {
      // The axios interceptor (src/utils/api.js) already surfaces the real
      // backend error message (e.g. "Archive this list before deleting
      // it") as a toast for every failed request — no second, generic
      // toast here.
      return false
    }
  }

  // Archive/unarchive send only the status. The backend keeps every field a
  // PUT leaves out, so re-sending the listing row's possibly stale name,
  // description and tags would overwrite someone else's edit.
  const archiveSuppressionList = row =>
    updateSuppressionList(row.id, { status: 'archived' }, `${listLabel(row.type)} archived successfully!`)

  const unarchiveSuppressionList = row =>
    updateSuppressionList(row.id, { status: 'active' }, `${listLabel(row.type)} moved back to active!`)

  // blockedByCampaigns maps a list id to the campaigns using it. Naming them
  // tells the user what to detach; the rest of skippedIds keep the generic
  // count (already deleted, not found, or an import in progress).
  const reportSkipped = (skippedIds, blockedByCampaigns, failedVerb) => {
    const blocked = blockedByCampaigns || {}
    const blockedIds = skippedIds.filter(id => (blocked[id] || []).length > 0)
    const otherCount = skippedIds.length - blockedIds.length
    if (blockedIds.length > 0) {
      const campaigns = [...new Set(blockedIds.flatMap(id => blocked[id]))]
      showError(
        `${blockedIds.length} suppression list${blockedIds.length === 1 ? '' : 's'} could not be ${failedVerb} because ${
          blockedIds.length === 1 ? 'it is' : 'they are'
        } in use by: ${campaigns.join(', ')}.`
      )
    }
    if (otherCount > 0) {
      showError(`${otherCount} suppression list${otherCount === 1 ? '' : 's'} could not be ${failedVerb}.`)
    }
  }

  // Asked when Delete is clicked, before the dialog opens. The backend applies
  // every guard the delete does (status, import in progress, campaign usage),
  // so safeToDelete is exactly what the delete would remove and each blocked
  // entry says why. Returns null on failure — the caller must not open the
  // dialog then. The axios interceptor already toasts the error.
  const checkSuppressionListUsage = async ids => {
    try {
      const response = await api.post(SuppressionListUsage, { listIds: ids })
      const { safeToDelete = [], blocked = [] } = response?.data?.data ?? {}
      return { safeToDelete, blocked }
    } catch {
      return null
    }
  }

  const bulkDeleteSuppressionLists = async ids => {
    try {
      const response = await api.delete(SuppressionListBulkDelete, { data: { listIds: ids } })
      const { deletedCount = 0, skippedIds = [], blockedByCampaigns } = response?.data ?? {}

      if (deletedCount > 0) {
        showSuccess(`${deletedCount} suppression list${deletedCount === 1 ? '' : 's'} deleted successfully!`)
      }
      reportSkipped(skippedIds, blockedByCampaigns, 'deleted')
      return response?.data ?? { totalRequested: ids.length, deletedCount: 0, deletedIds: [], skippedIds: ids }
    } catch {
      return { totalRequested: ids.length, deletedCount: 0, deletedIds: [], skippedIds: ids }
    }
  }

  const bulkUpdateSuppressionListsStatus = async (ids, status) => {
    const verb = status === 'archived' ? 'archived' : 'moved back to active'
    try {
      const response = await api.put(SuppressionListBulkStatus, { listIds: ids, status })
      const { updatedCount = 0, skippedIds = [], blockedByCampaigns } = response?.data ?? {}

      if (updatedCount > 0) {
        showSuccess(`${updatedCount} suppression list${updatedCount === 1 ? '' : 's'} ${verb}!`)
      }
      reportSkipped(skippedIds, blockedByCampaigns, 'updated')
      return response?.data ?? { totalRequested: ids.length, updatedCount: 0, updatedIds: [], skippedIds: ids }
    } catch {
      return { totalRequested: ids.length, updatedCount: 0, updatedIds: [], skippedIds: ids }
    }
  }

  const bulkArchiveSuppressionLists = ids => bulkUpdateSuppressionListsStatus(ids, 'archived')
  const bulkUnarchiveSuppressionLists = ids => bulkUpdateSuppressionListsStatus(ids, 'active')

  const getSuppressionListFilterList = async () => {
    try {
      const response = await api.get(SuppressionListFilters)
      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasSuppressionListTagFilter(!!tagsFilter)
      setSuppressionListFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])
      setSuppressionListOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })
      return initialSelectedFilters
    } catch {
      setSuppressionListFilterList([])
      setSuppressionListOtherFilterList([])
      setHasSuppressionListTagFilter(false)
      return {}
    }
  }

  const getMembers = async (listId, { page = 1, pageSize = 25, searchKeyword = '' } = {}) => {
    try {
      const response = await api.get(SuppressionListMembers.replace(':listId', listId), {
        params: { page, pageSize, searchKeyword }
      })
      setMembers(response?.data?.members ?? [])
      setMemberTotal(response?.data?.total ?? 0)
    } catch {
      // Clear rather than leave the previous list's rows on screen. The
      // dialog has already swapped in the new list's title and count, so
      // keeping stale rows rendered list B's header over list A's members —
      // a failure that looked like a successful load. The interceptor in
      // useAPI still raises the error toast.
      setMembers([])
      setMemberTotal(0)
    }
  }

  const updateMember = async (listId, memberId, payload) => {
    try {
      await api.patch(SuppressionListMemberById.replace(':listId', listId).replace(':memberId', memberId), payload)
      showSuccess('Member updated successfully!')
      return true
    } catch {
      return false
    }
  }

  const deleteMember = async (listId, memberId) => {
    try {
      await api.delete(SuppressionListMemberById.replace(':listId', listId).replace(':memberId', memberId))
      showSuccess('Member deleted successfully!')
      return true
    } catch {
      return false
    }
  }

  const detectImport = async formData => {
    try {
      const response = await api.post(SuppressionListImport, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const headers = response?.data?.detectedHeaders ?? []
      setDraftId(response?.data?.draftId ?? '')
      setDetectedHeaders(headers)
      // The Match step only seeds `userMapping` when it is empty (so Review ->
      // Back keeps hand-made mappings). Drop it here when the new upload's
      // columns differ, or Match kept rendering — and Next kept submitting —
      // the previous file's columns against this new draft.
      setUserMapping(prev =>
        prev.length === headers.length && prev.every((row, index) => row.header === headers[index]) ? prev : []
      )
      // The server parses the file it actually received, so this is the
      // only sample available for a zipped upload — the browser cannot
      // read one out of an archive.
      setSampleRow(response?.data?.sampleRow ?? [])
      // A paste with no header row comes back with generated column names,
      // and its first line is data the Match step must sample from.
      setImportHeaderless(Boolean(response?.data?.headerless))
      setGeneratedMapping(response?.data?.suggestedMapping ?? {})
      return response?.data ?? null
    } catch {
      return null
    }
  }

  const previewImport = async (draftIdToPreview, columnMapping) => {
    try {
      const response = await api.put(SuppressionListImport, {
        draftId: draftIdToPreview,
        columnMapping
      })
      setPreviewSummary(response?.data ?? null)
      return response?.data ?? null
    } catch {
      return null
    }
  }

  const commitImport = async draftIdToCommit => {
    try {
      const response = await api.post(SuppressionListImportCommit.replace(':draftId', draftIdToCommit))
      setCommitStatus('processing')
      return response?.data ?? null
    } catch {
      return null
    }
  }

  const pollImportStatus = async draftIdToPoll => {
    try {
      const response = await api.get(SuppressionListImportStatus.replace(':draftId', draftIdToPoll), {
        skipGlobalToast: true
      })
      setImportDraftStatus(response?.data ?? null)
      if (response?.data?.status === 'completed' || response?.data?.status === 'failed') {
        setCommitStatus(response.data.status)
      }
      return response?.data ?? null
    } catch (error) {
      // Report the failure instead of null: null read as "still processing",
      // so a broken status endpoint kept the wizard polling forever.
      return { status: 'error', httpStatus: error?.response?.status ?? null }
    }
  }

  // Stops waiting on an import stuck in processing. Resolves to the draft's
  // status, or to { conflict: true } when it is no longer processing (it
  // finished or failed meanwhile), or null on any other failure.
  const markImportFailed = async draftIdToFail => {
    try {
      const response = await api.post(SuppressionListImportFail.replace(':draftId', draftIdToFail), null, {
        skipGlobalToast: true
      })
      return response?.data ?? null
    } catch (error) {
      if (error?.response?.status === 409) {
        return { conflict: true }
      }
      return null
    }
  }

  // Adds members typed in on the Review step. Resolves to { ok, results,
  // addedCount }. A 400 for malformed identifiers still carries per-row
  // results, so the caller can mark the rows to fix; the toast is left to it.
  // With { dryRun: true } nothing is written: the results say what each row
  // would do, for the Review step to show before Finish. A dry run's
  // draftId has the rows checked against the staged file as well.
  const addMembers = async (listId, membersToAdd, { dryRun = false, draftId: stagedDraftId = '' } = {}) => {
    try {
      const response = await api.post(
        SuppressionListMembers.replace(':listId', listId),
        { members: membersToAdd, ...(dryRun && { dryRun: true, ...(stagedDraftId && { draftId: stagedDraftId }) }) },
        { skipGlobalToast: true }
      )
      return { ok: true, results: response?.data?.results ?? [], addedCount: response?.data?.addedCount ?? 0 }
    } catch (error) {
      const data = error?.response?.data
      return { ok: false, results: data?.results ?? [], message: data?.message || '' }
    }
  }

  const resetImportWizard = () => {
    setSelectedList(defaultProvider.selectedList)
    setWizardStep(defaultProvider.wizardStep)
    setDetailsName(defaultProvider.detailsName)
    setDetailsChannels(defaultProvider.detailsChannels)
    setDetailsIsBlackout(defaultProvider.detailsIsBlackout)
    setDetailsSavedType(defaultProvider.detailsSavedType)
    setDetailsTags(defaultProvider.detailsTags)
    setDetailsDescription(defaultProvider.detailsDescription)
    setDetailsError(defaultProvider.detailsError)
    setImportMethod(defaultProvider.importMethod)
    setFiles(defaultProvider.files)
    setCsvData(defaultProvider.csvData)
    setWizardListId(defaultProvider.wizardListId)
    setWizardAction(defaultProvider.wizardAction)
    setDraftId(defaultProvider.draftId)
    setDetectedHeaders(defaultProvider.detectedHeaders)
    setSampleRow(defaultProvider.sampleRow)
    setImportHeaderless(defaultProvider.importHeaderless)
    setGeneratedMapping(defaultProvider.generatedMapping)
    setUserMapping(defaultProvider.userMapping)
    setPreviewSummary(defaultProvider.previewSummary)
    setCommitStatus(defaultProvider.commitStatus)
    setImportDraftStatus(defaultProvider.importDraftStatus)
    setPendingMembers(defaultProvider.pendingMembers)
    setManualAddedCount(defaultProvider.manualAddedCount)
  }

  const values = useMemo(
    () => ({
      rows,
      setRows,
      rowCount,
      setRowCount,
      selectedList,
      setSelectedList,
      fetchSuppressionLists,
      createSuppressionList,
      getSuppressionListById,
      updateSuppressionList,
      deleteSuppressionList,
      checkSuppressionListUsage,
      bulkDeleteSuppressionLists,
      archiveSuppressionList,
      unarchiveSuppressionList,
      bulkArchiveSuppressionLists,
      bulkUnarchiveSuppressionLists,

      suppressionListFilterList,
      setSuppressionListFilterList,
      suppressionListOtherFilterList,
      setSuppressionListOtherFilterList,
      hasSuppressionListTagFilter,
      setHasSuppressionListTagFilter,
      getSuppressionListFilterList,

      members,
      setMembers,
      memberTotal,
      setMemberTotal,
      getMembers,
      updateMember,
      deleteMember,

      wizardStep,
      setWizardStep,
      detailsName,
      setDetailsName,
      detailsChannels,
      setDetailsChannels,
      detailsIsBlackout,
      setDetailsIsBlackout,
      detailsSavedType,
      setDetailsSavedType,
      detailsTags,
      setDetailsTags,
      detailsDescription,
      setDetailsDescription,
      detailsError,
      setDetailsError,

      importMethod,
      setImportMethod,
      files,
      setFiles,
      csvData,
      setCsvData,
      wizardListId,
      setWizardListId,
      wizardAction,
      setWizardAction,
      draftId,
      setDraftId,
      detectedHeaders,
      setDetectedHeaders,
      sampleRow,
      setSampleRow,
      importHeaderless,
      generatedMapping,
      setGeneratedMapping,
      userMapping,
      setUserMapping,
      previewSummary,
      setPreviewSummary,
      commitStatus,
      setCommitStatus,
      importDraftStatus,
      setImportDraftStatus,
      detectImport,
      previewImport,
      commitImport,
      pollImportStatus,
      markImportFailed,
      pendingMembers,
      setPendingMembers,
      manualAddedCount,
      setManualAddedCount,
      addMembers,
      resetImportWizard
    }),
    [
      rows,
      rowCount,
      selectedList,
      suppressionListFilterList,
      suppressionListOtherFilterList,
      hasSuppressionListTagFilter,
      members,
      memberTotal,
      wizardStep,
      detailsName,
      detailsChannels,
      detailsIsBlackout,
      detailsSavedType,
      detailsTags,
      detailsDescription,
      detailsError,
      importMethod,
      files,
      csvData,
      wizardListId,
      wizardAction,
      draftId,
      detectedHeaders,
      sampleRow,
      importHeaderless,
      generatedMapping,
      userMapping,
      previewSummary,
      commitStatus,
      importDraftStatus,
      pendingMembers,
      manualAddedCount
    ]
  )

  return <SuppressionListContext.Provider value={values}>{children}</SuppressionListContext.Provider>
}

SuppressionListProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export { SuppressionListContext, SuppressionListProvider, defaultProvider }
